import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Truck, Shield, Scissors, ArrowRight } from "lucide-react";
import { fetchProducts } from "../lib/api";
import { ProductCard } from "../components/ProductCard";

const heroImage = "/images/editorial/hero.jpg";

const storyImage = "/images/editorial/story.jpg";

const benefits = [
  {
    icon: Scissors,
    title: "Premium Quality",
    description:
      "Heavyweight fabrics, small-batch production, garments built to last seasons, not trends.",
  },
  {
    icon: Shield,
    title: "Comfortable Fit",
    description:
      "Relaxed silhouettes refined over dozens of fittings for true-to-size comfort every day.",
  },
  {
    icon: Truck,
    title: "Fast Worldwide Shipping",
    description:
      "Dispatched within 24 hours. Free delivery on all international orders over $100.",
  },
];

export default function HomePage() {
  const [featured, setFeatured] = useState([]);

  useEffect(() => {
    fetchProducts({ featured: true })
      .then((res) => setFeatured(Array.isArray(res) ? res : []))
      .catch(() => setFeatured([]));
  }, []);

  return (
    <div data-testid="home-page">
      {/* Hero */}
      <section className="relative h-[82vh] min-h-[560px] w-full overflow-hidden">
        <img
          src={heroImage}
          alt="Simple Style Mode editorial"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-black/20" />
        <div className="relative z-10 max-w-7xl mx-auto h-full px-6 md:px-12 flex items-end pb-16 md:pb-24">
          <div className="max-w-2xl fade-up">
            <div className="label-xs text-white/90 mb-4">New Arrivals — SS26</div>
            <h1 className="font-display text-white text-5xl sm:text-6xl md:text-7xl leading-[0.95] font-medium tracking-tight">
              Timeless essentials.<br />Minimal design.
            </h1>
            <p className="text-white/90 text-base md:text-lg mt-6 max-w-md leading-relaxed">
              Quiet wardrobe staples cut from considered fabrics. No logos.
              No shouting. Just pieces you'll wear for years.
            </p>
            <div className="mt-10 flex flex-wrap gap-3">
              <Link
                to="/shop"
                data-testid="hero-shop-btn"
                className="inline-flex items-center gap-3 px-8 py-4 bg-white text-black text-xs uppercase tracking-[0.25em] hover:bg-neutral-200 transition-colors"
              >
                Shop the Collection <ArrowRight size={14} />
              </Link>
              <Link
                to="/about"
                data-testid="hero-about-btn"
                className="inline-flex items-center px-8 py-4 border border-white text-white text-xs uppercase tracking-[0.25em] hover:bg-white hover:text-black transition-colors"
              >
                Our Story
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Marquee */}
      <section className="border-y border-[color:var(--ssm-border)] py-4 overflow-hidden">
        <div className="marquee">
          {[
            "Free worldwide shipping over $100",
            "30-day hassle-free returns",
            "Unbranded, in-house design",
            "Small-batch production",
            "Free worldwide shipping over $100",
            "30-day hassle-free returns",
            "Unbranded, in-house design",
            "Small-batch production",
          ].map((t, i) => (
            <span key={i} className="label-xs text-neutral-700">
              — {t}
            </span>
          ))}
        </div>
      </section>

      {/* Benefits */}
      <section className="max-w-7xl mx-auto px-6 md:px-12 py-24 md:py-32">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-12">
          {benefits.map((b) => (
            <div key={b.title} data-testid={`benefit-${b.title.toLowerCase().replace(/\s+/g, "-")}`}>
              <b.icon size={24} strokeWidth={1.2} />
              <div className="label-xs mt-6">0{benefits.indexOf(b) + 1}</div>
              <h3 className="font-display text-2xl md:text-3xl font-medium mt-2 mb-3">
                {b.title}
              </h3>
              <p className="text-sm text-neutral-600 leading-relaxed max-w-sm">
                {b.description}
              </p>
            </div>
          ))}
        </div>
      </section>

      {/* Featured Products */}
      <section className="max-w-7xl mx-auto px-6 md:px-12 pb-24 md:pb-32">
        <div className="flex items-end justify-between mb-12">
          <div>
            <div className="label-xs mb-3">Featured</div>
            <h2 className="font-display text-4xl md:text-5xl font-medium leading-none">
              The essentials
            </h2>
          </div>
          <Link
            to="/shop"
            data-testid="home-view-all-link"
            className="hidden md:inline-flex items-center gap-2 text-xs uppercase tracking-[0.25em] ssm-underline-link"
          >
            View all <ArrowRight size={14} />
          </Link>
        </div>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-x-6 gap-y-12">
          {featured.map((p) => (
            <ProductCard key={p.id} product={p} />
          ))}
        </div>
      </section>

      {/* Story */}
      <section className="bg-[color:var(--ssm-surface)]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-24 md:py-32 grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-16 items-center">
          <div className="md:col-span-6 aspect-[4/5] overflow-hidden">
            <img
              src={storyImage}
              alt="Simple Style Mode studio"
              className="w-full h-full object-cover"
            />
          </div>
          <div className="md:col-span-6">
            <div className="label-xs mb-3">Our Philosophy</div>
            <h2 className="font-display text-4xl md:text-5xl font-medium leading-tight mb-6">
              Considered garments,<br />built to last.
            </h2>
            <p className="text-neutral-600 leading-relaxed mb-4">
              Simple Style Mode creates minimalist clothing designed for
              everyday wear. Our focus is on quality basics, clean design, and
              timeless style — pieces that quietly earn their place in your
              wardrobe season after season.
            </p>
            <p className="text-neutral-600 leading-relaxed mb-8">
              Every garment is designed in-house and produced in small batches.
              All products are unbranded or sold under our own brand. No
              third-party logos, no licensed collaborations — just honest,
              wearable essentials.
            </p>
            <Link
              to="/about"
              data-testid="home-story-about-btn"
              className="inline-flex items-center gap-3 px-8 py-4 border border-black text-xs uppercase tracking-[0.25em] hover:bg-black hover:text-white transition-colors"
            >
              Read our story <ArrowRight size={14} />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
